import { InjectionToken } from '@angular/core';
import { EProductSearchSearchOrderBy } from '@api-ui-app/src/app/e-commerce-product-templates/types/e-product-search-order-by.enum';
import { EProductSearchSearchOrder } from '@api-ui-app/src/app/e-commerce-product-templates/types/e-product-search-order.enum';
import { EProductTemplateUsageEventStep } from '@api-ui-app/src/app/e-commerce-product-templates/types/e-product-template-usage-event-step.enum';
import { EProductTemplatesListItem } from '@api-ui-app/src/app/e-commerce-product-templates/types/e-product-templates-list-item';
import { EProductTemplatesSearchParams } from '@api-ui-app/src/app/e-commerce-product-templates/types/e-product-templates-search-params';
import { ActionReducerMap, createReducer, on } from '@ngrx/store';
import {
  addProductTemplateToStore,
  addProductTemplateToStoreFailure,
  addProductTemplateToStoreSuccess,
  createProductTemplate,
  createProductTemplateFailure,
  createProductTemplateSuccess,
  deleteProductTemplate,
  deleteProductTemplateFailure,
  deleteProductTemplateSuccess,
  duplicateProductTemplate,
  duplicateProductTemplateFailure,
  duplicateProductTemplateSuccess,
  editProductTemplate,
  editProductTemplateSuccess,
  load,
  loadSuccess,
  logProductTemplateUsageInitiatedEvent,
  renameProductTemplate,
  renameProductTemplateFailure,
  renameProductTemplateSuccess,
  setLoadingState,
  setSearchParams,
  setSortingParams,
  setTemplateName,
} from './e-commerce-product-templates.actions';

export const eCommerceProductTemplatesFeatureKey = 'eCommerceProductTemplates';

export interface State {
  isLoading: boolean;
  isProcessing: boolean;
  searchParams: EProductTemplatesSearchParams;
  items: EProductTemplatesListItem[];
  productTemplateUsageStep: EProductTemplateUsageEventStep;
  productTemplateUsageInitiatedAt: number;
}

export const initialState: State = {
  isLoading: false,
  isProcessing: false,
  searchParams: {
    templateName: null,
    sortingParams: {
      order: EProductSearchSearchOrder.DESC,
      orderBy: EProductSearchSearchOrderBy.UPDATED_AT,
    },
  },
  items: null,
  productTemplateUsageStep: null,
  productTemplateUsageInitiatedAt: null,
};

export const reducer = createReducer(
  initialState,
  on(
    setSearchParams,
    (state, action): State => ({
      ...state,
      searchParams: action.payload,
    }),
  ),
  on(
    setTemplateName,
    (state, action): State => ({
      ...state,
      searchParams: {
        ...state.searchParams,
        templateName: action.payload,
      },
    }),
  ),
  on(
    setSortingParams,
    (state, action): State => ({
      ...state,
      searchParams: {
        ...state.searchParams,
        sortingParams: action.payload,
      },
    }),
  ),
  on(
    load,
    (state, action): State => ({
      ...state,
      isLoading: true,
      searchParams: action.searchParams,
    }),
  ),
  on(loadSuccess, (state, action): State => {
    const { searchParams, offset } = action;
    const items = offset > 0 ? [...(state?.items || []), ...(action.items || [])] : action.items;

    return {
      ...state,
      isLoading: false,
      searchParams,
      items,
    };
  }),
  on(
    duplicateProductTemplate,
    renameProductTemplate,
    deleteProductTemplate,
    addProductTemplateToStore,
    createProductTemplate,
    editProductTemplate,
    (state): State => ({
      ...state,
      isProcessing: true,
    }),
  ),
  on(
    duplicateProductTemplateSuccess,
    duplicateProductTemplateFailure,
    renameProductTemplateSuccess,
    renameProductTemplateFailure,
    deleteProductTemplateSuccess,
    deleteProductTemplateFailure,
    addProductTemplateToStoreSuccess,
    addProductTemplateToStoreFailure,
    createProductTemplateSuccess,
    createProductTemplateFailure,
    editProductTemplateSuccess,
    editProductTemplateSuccess,
    (state): State => ({
      ...state,
      isProcessing: false,
    }),
  ),
  on(
    logProductTemplateUsageInitiatedEvent,
    (state, action): State => ({
      ...state,
      productTemplateUsageStep: action.step,
      productTemplateUsageInitiatedAt: new Date().getTime(),
    }),
  ),
  on(
    setLoadingState,
    (state, { isLoading }): State => ({
      ...state,
      isLoading,
    }),
  ),
);

export const eCommerceProductTemplatesReducerToken = new InjectionToken<ActionReducerMap<State>>(
  'product templates reducer',
);
